import React, { useCallback, useEffect, useRef, useState } from "react";

import { Box, Collapse, Flex, Icon, Text } from "@chakra-ui/react";

import { FaMinus, FaPlus } from "react-icons/fa";

interface AdvancedFilterProps {
  heading: string;
  children: React.ReactNode;
  isActive?: boolean;
  onReset?: () => void;
  isCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  className?: string;
  resetKey?: number;
}

export const AdvancedFilter: React.FC<AdvancedFilterProps> = ({
  heading,
  children,
  isActive,
  onReset,
  isCollapsed,
  onCollapseChange,
  className,
  resetKey
}) => {
  const [isManuallyOpen, setIsManuallyOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isCollapsed !== undefined) {
      setIsManuallyOpen(!isCollapsed);
    }
  }, [setIsManuallyOpen, isCollapsed]);

  React.useEffect(() => {
    if (resetKey !== undefined) {
      setIsManuallyOpen(false);
    }
  }, [resetKey]);

  // Focus on first input when section is manually opened
  useEffect(() => {
    if (isManuallyOpen && !isActive && contentRef.current) {
      // Small delay to ensure the collapse animation has started
      const timer = setTimeout(() => {
        const firstInput = contentRef.current?.querySelector(
          "input, select"
        ) as HTMLInputElement | HTMLSelectElement;
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isManuallyOpen, isActive]);

  const handleIconClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isActive && onReset) {
        setIsManuallyOpen(false);
        onReset();
      } else {
        const newState = !isManuallyOpen;
        setIsManuallyOpen(newState);
        onCollapseChange?.(!newState);
      }
    },
    [isActive, isManuallyOpen, onReset, onCollapseChange]
  );

  const handleHeaderClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!isActive) {
        const newState = !isManuallyOpen;
        setIsManuallyOpen(newState);
        onCollapseChange?.(!newState);
      }
    },
    [isActive, isManuallyOpen, onCollapseChange]
  );

  const shouldShowContent = isActive || isManuallyOpen;

  return (
    <Box px={2}>
      <Flex
        as={isActive ? "div" : "button"}
        onClick={handleHeaderClick}
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        pr={1}
        py={2}
        cursor={isActive ? "default" : "pointer"}
        _hover={!isActive ? { opacity: 0.8 } : undefined}
      >
        <Text
          fontSize="sm"
          fontWeight="medium"
          color={isActive ? "blue.500" : "inherit"}
        >
          {heading}
        </Text>
        <Icon
          as={isActive || isManuallyOpen ? FaMinus : FaPlus}
          boxSize={3}
          color={isActive ? "blue.500" : "inherit"}
          onClick={handleIconClick}
          cursor="pointer"
        />
      </Flex>
      <Collapse in={shouldShowContent} animateOpacity className={className}>
        <Box pt={2} ref={contentRef}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};
